import * as React from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';

import { AppShell } from '@/app/shell/app-shell';
import { PageAccessBoundary, RoleAwareIndex } from '@/app/routes/guards';
import { RouteFallback } from '@/app/routes/route-fallback';
import { LegacyMemoryRedirect } from '@/pages/memory/legacy-redirect';
import { NotFoundPage } from '@/pages/not-found';

/**
 * Routes are lazy so the initial bundle only carries the shell. The heavy pages
 * (conference detail, memory engine, business config) are the ones that most
 * benefit, and they are also the ones a given user may never open.
 */
const DashboardPage = React.lazy(() => import('@/pages/dashboard/dashboard-page'));
const ConferenceListPage = React.lazy(() => import('@/pages/conferences/conference-list-page'));
const ConferenceDetailPage = React.lazy(() => import('@/pages/conferences/conference-detail-page'));
const UserListPage = React.lazy(() => import('@/pages/users/user-list-page'));
const UserDetailPage = React.lazy(() => import('@/pages/users/user-detail-page'));
const BusinessOverviewPage = React.lazy(() => import('@/pages/config/business-overview-page'));
const BusinessConfigPage = React.lazy(() => import('@/pages/config/business-config-page'));
const VoiceAgentPage = React.lazy(() => import('@/pages/config/voice-agent-page'));
const PolicyViolationsPage = React.lazy(() => import('@/pages/ops/policy-violations-page'));
const PolicyViolationDetailPage = React.lazy(
  () => import('@/pages/ops/policy-violation-detail-page'),
);
const PerformanceInboxPage = React.lazy(() => import('@/pages/ops/performance-inbox-page'));
const PerformanceEventDetailPage = React.lazy(
  () => import('@/pages/ops/performance-event-detail-page'),
);
const ManualReviewPage = React.lazy(() => import('@/pages/ops/manual-review-page'));
const ManualReviewDetailPage = React.lazy(() => import('@/pages/ops/manual-review-detail-page'));
const RoutingAuditPage = React.lazy(() => import('@/pages/ops/routing-audit-page'));
const RoutingAuditDetailPage = React.lazy(() => import('@/pages/ops/routing-audit-detail-page'));
const VoicemailsWorkspacePage = React.lazy(() => import('@/pages/ops/voicemails-workspace-page'));
const ContactsPage = React.lazy(() => import('@/pages/ops/contacts-page'));
const ContactDetailPage = React.lazy(() => import('@/pages/ops/contact-detail-page'));
const ContactImportPage = React.lazy(() => import('@/pages/ops/contact-import-page'));
const DepartmentsPage = React.lazy(() => import('@/pages/ops/departments-page'));
const DepartmentDetailPage = React.lazy(() => import('@/pages/ops/department-detail-page'));
const SipPhonesPage = React.lazy(() => import('@/pages/ops/sip-phones-page'));
const SipPhoneDetailPage = React.lazy(() => import('@/pages/ops/sip-phone-detail-page'));
const UserSessionsPage = React.lazy(() => import('@/pages/ops/user-sessions-page'));
const StorageBrowserPage = React.lazy(() => import('@/pages/ops/storage-browser-page'));
const MemoryModelPage = React.lazy(() => import('@/pages/memory/memory-model-page'));
const MemoryLibraryPage = React.lazy(() => import('@/pages/memory/library-page'));
const MemoryHistoryPage = React.lazy(() => import('@/pages/memory/history-page-route'));
const MemoryDuplicatesPage = React.lazy(() => import('@/pages/memory/duplicates-page'));
const MemoryMergePage = React.lazy(() => import('@/pages/memory/merge-page'));
const MemoryEvalsPage = React.lazy(() => import('@/pages/memory/evals-page'));
const WeightMatrixPage = React.lazy(() => import('@/pages/evaluation/weight-matrix-page'));
const CategorizationPage = React.lazy(() => import('@/pages/evaluation/categorization-page'));
const RuleCatalogPage = React.lazy(() => import('@/pages/evaluation/rule-catalog-page'));
const GenericModelPage = React.lazy(() => import('@/pages/data/generic-model-page'));
const GenericObjectPage = React.lazy(() => import('@/pages/data/generic-object-page'));
const RegistryIndexPage = React.lazy(() => import('@/pages/data/registry-index-page'));
const AppearancePage = React.lazy(() => import('@/pages/settings/appearance-page'));

export function AppRoutes() {
  return (
    <Routes>
      <Route element={<AppShell />}>
        {/* One gate for every page route: see PageAccessBoundary. */}
        <Route element={<PageAccessBoundary />}>
          <Route index element={<RoleAwareIndex />} />

          <Route
            path="dashboard"
            element={
              <RouteFallback>
                <DashboardPage />
              </RouteFallback>
            }
          />

          {/* Conferences */}
          <Route
            path="calls"
            element={
              <RouteFallback>
                <ConferenceListPage />
              </RouteFallback>
            }
          />
          <Route
            path="calls/:pk"
            element={
              <RouteFallback>
                <ConferenceDetailPage />
              </RouteFallback>
            }
          />

          {/* People */}
          <Route
            path="users"
            element={
              <RouteFallback>
                <UserListPage />
              </RouteFallback>
            }
          />
          <Route
            path="users/:pk"
            element={
              <RouteFallback>
                <UserDetailPage />
              </RouteFallback>
            }
          />

          {/* Configuration */}
          <Route
            path="config/business"
            element={
              <RouteFallback>
                <BusinessOverviewPage />
              </RouteFallback>
            }
          />
          <Route
            path="config/business/settings"
            element={
              <RouteFallback>
                <BusinessConfigPage />
              </RouteFallback>
            }
          />
          <Route
            path="config/voice-agent"
            element={
              <RouteFallback>
                <VoiceAgentPage />
              </RouteFallback>
            }
          />

          {/* Review queues */}
          <Route
            path="queues/policy-violations"
            element={
              <RouteFallback>
                <PolicyViolationsPage />
              </RouteFallback>
            }
          />
          <Route
            path="queues/policy-violations/:pk"
            element={
              <RouteFallback>
                <PolicyViolationDetailPage />
              </RouteFallback>
            }
          />
          <Route
            path="queues/performance-inbox"
            element={
              <RouteFallback>
                <PerformanceInboxPage />
              </RouteFallback>
            }
          />
          <Route
            path="queues/performance-inbox/:pk"
            element={
              <RouteFallback>
                <PerformanceEventDetailPage />
              </RouteFallback>
            }
          />
          <Route
            path="queues/manual-review"
            element={
              <RouteFallback>
                <ManualReviewPage />
              </RouteFallback>
            }
          />
          <Route
            path="queues/manual-review/:pk"
            element={
              <RouteFallback>
                <ManualReviewDetailPage />
              </RouteFallback>
            }
          />
          <Route
            path="queues/routing-audit"
            element={
              <RouteFallback>
                <RoutingAuditPage />
              </RouteFallback>
            }
          />
          <Route
            path="queues/routing-audit/:pk"
            element={
              <RouteFallback>
                <RoutingAuditDetailPage />
              </RouteFallback>
            }
          />

          {/* Operations */}
          {/* One workspace for the list and the selected message; the pk only
              chooses the selection, so both paths render the same page. */}
          <Route
            path="ops/voicemails"
            element={
              <RouteFallback>
                <VoicemailsWorkspacePage />
              </RouteFallback>
            }
          />
          <Route
            path="ops/voicemails/:pk"
            element={
              <RouteFallback>
                <VoicemailsWorkspacePage />
              </RouteFallback>
            }
          />
          <Route
            path="ops/contacts"
            element={
              <RouteFallback>
                <ContactsPage />
              </RouteFallback>
            }
          />
          <Route
            path="ops/contacts/imports/:jobId?"
            element={<RouteFallback><ContactImportPage /></RouteFallback>}
          />
          <Route
            path="ops/contacts/:pk"
            element={
              <RouteFallback>
                <ContactDetailPage />
              </RouteFallback>
            }
          />
          <Route
            path="ops/departments"
            element={
              <RouteFallback>
                <DepartmentsPage />
              </RouteFallback>
            }
          />
          <Route
            path="ops/departments/:pk"
            element={
              <RouteFallback>
                <DepartmentDetailPage />
              </RouteFallback>
            }
          />
          <Route
            path="ops/sip-phones"
            element={
              <RouteFallback>
                <SipPhonesPage />
              </RouteFallback>
            }
          />
          <Route
            path="ops/sip-phones/:pk"
            element={
              <RouteFallback>
                <SipPhoneDetailPage />
              </RouteFallback>
            }
          />
          <Route
            path="ops/sessions"
            element={
              <RouteFallback>
                <UserSessionsPage />
              </RouteFallback>
            }
          />
          <Route
            path="ops/storage"
            element={
              <RouteFallback>
                <StorageBrowserPage />
              </RouteFallback>
            }
          />

          {/* Memory engine. Its operational dashboard merged into /dashboard. */}
          <Route path="memory" element={<Navigate to="/dashboard" replace />} />
          <Route
            path="memory/library"
            element={<RouteFallback><MemoryLibraryPage /></RouteFallback>}
          />
          {/* Needs Review and Approved are one Library now. Old deep links
              redirect rather than 404, carrying their query string with them. */}
          <Route path="memory/inbox" element={<LegacyMemoryRedirect from="inbox" />} />
          <Route path="memory/approved" element={<LegacyMemoryRedirect from="approved" />} />
          <Route
            path="memory/duplicates"
            element={<RouteFallback><MemoryDuplicatesPage /></RouteFallback>}
          />
          <Route
            path="memory/duplicates/:groupId"
            element={<RouteFallback><MemoryMergePage /></RouteFallback>}
          />
          <Route
            path="memory/history"
            element={<RouteFallback><MemoryHistoryPage /></RouteFallback>}
          />
          <Route
            path="memory/impact"
            element={<Navigate to="/dashboard" replace />}
          />
          <Route path="memory/memorycandidate" element={<LegacyMemoryRedirect from="inbox" />} />
          <Route path="memory/memoryitem" element={<LegacyMemoryRedirect from="approved" />} />
          {/* Memory Evals: the gate, the cases it grades and the runs that open
              it, on one page. The two generic eval model pages land here. */}
          <Route
            path="memory/evals"
            element={<RouteFallback><MemoryEvalsPage /></RouteFallback>}
          />
          <Route path="memory/memoryevalcase" element={<Navigate to="/memory/evals" replace />} />
          <Route path="memory/memoryevalrun" element={<Navigate to="/memory/evals" replace />} />
          <Route
            path="memory/:model"
            element={
              <RouteFallback>
                <MemoryModelPage />
              </RouteFallback>
            }
          />

          {/* Evaluation config */}
          <Route
            path="evaluation/weight-matrix"
            element={
              <RouteFallback>
                <WeightMatrixPage />
              </RouteFallback>
            }
          />
          <Route
            path="evaluation/categorization"
            element={
              <RouteFallback>
                <CategorizationPage />
              </RouteFallback>
            }
          />
          <Route
            path="evaluation/metrics"
            element={
              <RouteFallback>
                <RuleCatalogPage section="metrics" />
              </RouteFallback>
            }
          />
          <Route
            path="evaluation/policy-rules"
            element={
              <RouteFallback>
                <RuleCatalogPage section="policy" />
              </RouteFallback>
            }
          />
          <Route
            path="evaluation/events"
            element={
              <RouteFallback>
                <RuleCatalogPage section="events" />
              </RouteFallback>
            }
          />

          {/* Generic metadata-driven tables for every remaining model. */}
          <Route
            path="data"
            element={
              <RouteFallback>
                <RegistryIndexPage />
              </RouteFallback>
            }
          />
          <Route
            path="data/:label"
            element={
              <RouteFallback>
                <GenericModelPage />
              </RouteFallback>
            }
          />
          <Route
            path="data/:label/:pk"
            element={
              <RouteFallback>
                <GenericObjectPage />
              </RouteFallback>
            }
          />

          <Route
            path="settings/appearance"
            element={
              <RouteFallback>
                <AppearancePage />
              </RouteFallback>
            }
          />

          <Route path="*" element={<NotFoundPage />} />
        </Route>
      </Route>
    </Routes>
  );
}
